
import React, { useState } from 'react';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { Navigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { useIsMobile } from '@/hooks/use-mobile';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Shield, AlertTriangle, FileText, Users, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useContentModeration } from '@/hooks/useContentModeration';
import ApprovalRequestsTab from '@/components/admin/moderation/ApprovalRequestsTab';
import UserReportsTab from '@/components/admin/moderation/UserReportsTab';
import ContentReportsTab from '@/components/admin/moderation/ContentReportsTab';
import SecurityIncidentsTab from '@/components/admin/moderation/SecurityIncidentsTab';

const AdminContentModeration = () => {
  const { user } = useInternalAuth();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('approval-requests');
  
  const {
    approvalRequests,
    userReports,
    contentReports,
    securityIncidents,
    loading,
    loadAllData,
    updateApprovalRequest,
    updateUserReport,
    updateContentReport,
    updateSecurityIncident
  } = useContentModeration();

  if (!user || user.role !== 'admin') {
    return <Navigate to="/login" replace />;
  }

  // Count pending items for each category
  const pendingApprovals = approvalRequests.filter(req => req.status === 'pending').length;
  const activeUserReports = userReports.filter(report => ['pending', 'investigating'].includes(report.status)).length;
  const pendingContentReports = contentReports.filter(report => ['pending', 'reviewing'].includes(report.status)).length;
  const activeIncidents = securityIncidents.filter(incident => !incident.resolved).length;

  return (
    <div className="flex flex-col min-h-screen bg-black w-full">
      {!isMobile && <Header />}
      {isMobile && <MobileHeader />}
      
      <main className={`flex-grow w-full px-4 md:px-6 ${
        isMobile 
          ? 'mt-16 mb-16 py-4' 
          : 'mt-24 mb-6 py-6 md:py-12'
      }`}>
        <div className="w-full max-w-7xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => window.history.back()}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <h1 className="text-2xl font-bold text-white">Content Moderation</h1>
            </div>
            <Button
              onClick={loadAllData}
              disabled={loading}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Pending Approvals</p>
                    <p className="text-2xl font-bold text-white">{pendingApprovals}</p>
                  </div>
                  <Users className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">User Reports</p>
                    <p className="text-2xl font-bold text-white">{activeUserReports}</p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Content Reports</p>
                    <p className="text-2xl font-bold text-white">{pendingContentReports}</p>
                  </div>
                  <FileText className="h-8 w-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Security Incidents</p>
                    <p className="text-2xl font-bold text-white">{activeIncidents}</p>
                  </div>
                  <Shield className="h-8 w-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Moderation Tabs */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Shield className="h-5 w-5 text-sports-green" />
                Moderation Dashboard
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-4 bg-gray-700">
                  <TabsTrigger 
                    value="approval-requests" 
                    className="data-[state=active]:bg-sports-green data-[state=active]:text-black"
                  >
                    Approvals {pendingApprovals > 0 && `(${pendingApprovals})`}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="user-reports"
                    className="data-[state=active]:bg-sports-green data-[state=active]:text-black"
                  >
                    User Reports {activeUserReports > 0 && `(${activeUserReports})`}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="content-reports"
                    className="data-[state=active]:bg-sports-green data-[state=active]:text-black"
                  >
                    Content {pendingContentReports > 0 && `(${pendingContentReports})`}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="security-incidents"
                    className="data-[state=active]:bg-sports-green data-[state=active]:text-black"
                  >
                    Security {activeIncidents > 0 && `(${activeIncidents})`}
                  </TabsTrigger>
                </TabsList>

                <div className="mt-6">
                  <TabsContent value="approval-requests">
                    <ApprovalRequestsTab 
                      requests={approvalRequests}
                      onUpdateRequest={updateApprovalRequest}
                    />
                  </TabsContent>

                  <TabsContent value="user-reports">
                    <UserReportsTab 
                      reports={userReports}
                      onUpdateReport={updateUserReport}
                    />
                  </TabsContent>

                  <TabsContent value="content-reports">
                    <ContentReportsTab 
                      reports={contentReports}
                      onUpdateReport={updateContentReport}
                    />
                  </TabsContent>

                  <TabsContent value="security-incidents">
                    <SecurityIncidentsTab 
                      incidents={securityIncidents}
                      onUpdateIncident={updateSecurityIncident}
                    />
                  </TabsContent>
                </div>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </main>
      
      {!isMobile && <Footer />}
      {isMobile && <MobileBottomNav />}
    </div>
  );
};

export default AdminContentModeration;
